import { graphql } from "@repo/graphql-types/gql";
import type { ReactElement } from "react";
import {
  type RouterState,
  useRouterState,
  useSearch,
  useRouter,
} from "@tanstack/react-router";
import { useAtom } from "jotai";
import { ActiveAllocationDetailsQueryDocument } from "@repo/graphql-types/graphql";
import { ReferralBannerDrawer } from "@/components/referral/banner/referral-banner-drawer";
import { useGraphQL, useInvalidateQuery } from "@/hooks/use-graphql";
import type { FileRouteTypes } from "@/route-tree.gen";
import { ReturnRoomDrawer } from "@/components/room/return-room-drawer";
import { UnexpectedErrorDrawer } from "@/components/ui/drawer/unexpected-error-drawer";
import { ReturnRoomActionSearchParams } from "@/lib/action-schemas/return-room";
import { CsatAllocatedRoomDrawer } from "@/components/room/csat-allocated-room-drawer";
import {
  AllocatedRoomBar,
  type AllocatedRoomBarTrackParams,
} from "@/components/room/allocated/allocated-room-bar";
import { allocatedRoomBarIsOpenAtom } from "@/lib/atoms/allocated-room-bar";

const ActiveAllocationQuery = graphql(/* GraphQL */ `
  query ActiveAllocationQuery {
    activeAllocation: AgendamentosAlocacoesAtivas {
      horaInicio
      appointment: Agendamento {
        data
        codAgendamento
        horaFim
        horaInicio
        ...ReturnRoomDrawerFragment
      }
      locationRoom: UnidadeSala {
        nome
      }
    }
  }
`);

const CsatCategoriesQuery = graphql(/* GraphQL */ `
  query CsatCategoriesQuery {
    ...GetAllCsatCategoriesQueryFragment
  }
`);

const isEnabledRoute = (routerState: RouterState): boolean => {
  const { location } = routerState;
  const visibleRoutes: FileRouteTypes["fullPaths"][] = [
    "/home/",
    "/calendar/",
    "/waiting-room/",
    "/patients/",
    "/settings/",
  ];

  return visibleRoutes.some(
    (x) => location.pathname === x || location.pathname === x.replace(/\/$/, ""),
  );
};

const ONE_HOUR_STALE_TIME = 3600000;

const AllocatedRoomBarController = (): ReactElement | null => {
  const { data: allocation } = useGraphQL(ActiveAllocationQuery, undefined, {
    refetchInterval: 10_000,
  });
  const { data: csatCategories } = useGraphQL(CsatCategoriesQuery, undefined, {
    staleTime: ONE_HOUR_STALE_TIME,
  });

  const router = useRouter();
  const routerState = useRouterState();
  const searchParams: Record<string, unknown> = useSearch({ strict: false });
  const enabled = isEnabledRoute(routerState);

  const [roomBarIsOpenAtom, roomBarIsOpenSetAtom] = useAtom(allocatedRoomBarIsOpenAtom);

  const invalidateActiveAllocationDetailsQuery = useInvalidateQuery(
    ActiveAllocationDetailsQueryDocument,
  );

  const navigateToReturnRoomWithParams = (params: AllocatedRoomBarTrackParams): void => {
    invalidateActiveAllocationDetailsQuery();

    void router.navigate({
      to: ".",
      search: {
        ...params,
        ...searchParams,
        ...ReturnRoomActionSearchParams,
      },
    });
  };

  if (!enabled) {
    return null;
  }

  const activeAllocation = allocation?.activeAllocation[0];

  if (activeAllocation && roomBarIsOpenAtom === undefined) {
    roomBarIsOpenSetAtom(true);
  } else if (!activeAllocation) {
    roomBarIsOpenSetAtom(undefined);
  }

  const isCsatOpen = searchParams.action === "csat";
  const isReferralBannerOpen = searchParams.action === "referral-banner";

  const clearActionParam = (): void => {
    void router.navigate({
      to: ".",
      search: { ...searchParams, action: undefined },
      replace: true,
    });
  };

  const closeModal = (): void => {
    if (isCsatOpen || isReferralBannerOpen) {
      clearActionParam();
    }
  };

  const onModalClose = (isOpening: boolean): void => {
    if (isOpening) {
      return;
    }

    closeModal();
  };

  const openReferralBanner = (): void => {
    void router.navigate({
      to: ".",
      search: {
        ...searchParams,
        action: "referral-banner",
      },
      replace: true,
    });
  };

  if (!activeAllocation) {
    return (
      <>
        <CsatAllocatedRoomDrawer
          open={isCsatOpen}
          setOpen={onModalClose}
          data={csatCategories}
          onFiveStars={openReferralBanner}
        />
        {isReferralBannerOpen ? (
          <ReferralBannerDrawer open={isReferralBannerOpen} setOpen={onModalClose} />
        ) : null}
      </>
    );
  }

  const { appointment } = activeAllocation;

  const handleErrorDrawerDismiss = (value: boolean): void => {
    if (!value && searchParams.action === "error") {
      router.history.back();
    }
  };

  return (
    <>
      <AllocatedRoomBar onClickButtonReturnRoom={navigateToReturnRoomWithParams} />
      <ReturnRoomDrawer data={appointment} />
      <UnexpectedErrorDrawer
        open={searchParams.action === "error"}
        setOpen={handleErrorDrawerDismiss}
      />
    </>
  );
};

export { AllocatedRoomBarController };
